import {Inject, Injectable, NgZone, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {firstValueFrom, Observable, Subject} from 'rxjs';
import {fetchEventSource} from '@microsoft/fetch-event-source';
import {environment} from '../../../environments/environment';
import {EventSourceInterface} from '../interfaces/event-source.interface';

type Closeable = {close(): void};
class ForbiddenStream extends Error {}

@Injectable({providedIn: 'root'})
export class EventSourceService {
  public eventSources: Closeable[] = [];
  private streams = new Map<string, Subject<EventSourceInterface<any>>>();
  private observables = new Map<string, Observable<EventSourceInterface<any>>>();
  private sources = new Map<string, Closeable>();
  private credentials = new Map<string, Promise<{token: string; expiresAt: number}>>();

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object,
              private readonly ngZone: NgZone, private readonly http: HttpClient) {}

  public listenSingleTopic<T>(topic: string, closable = true, ngswBypass = true): Observable<EventSourceInterface<T>> {
    return this.listen<T>([topic], closable, ngswBypass);
  }

  public listenArrayOfTopics<T>(topics: string[], closable = true, ngswBypass = true): Observable<EventSourceInterface<T>> {
    return this.listen<T>(topics.map(topic => topic.replace(/^topic=/, '')), closable, ngswBypass);
  }

  private url(topics: string[], bypass: boolean): string {
    const params = new URLSearchParams();
    topics.forEach(topic => params.append('topic', topic));
    if (bypass) params.set('ngsw-bypass', 'true');
    return environment.mercureUrl + '?' + params.toString();
  }

  private listen<T>(topics: string[], closable: boolean, bypass: boolean): Observable<EventSourceInterface<T>> {
    const url = this.url(topics, bypass);
    const previous = this.observables.get(url);
    if (previous) return previous;
    const stream = new Subject<EventSourceInterface<T>>();
    if (!isPlatformBrowser(this.platformId)) return stream;
    this.streams.set(url, stream);
    const privateTopics = topics.filter(topic => /^\/(users\/[^/]+\/(notifications|conversations|unread-count)|conversations\/[^/]+\/messages)$/.test(topic));
    let closed = false;
    let controller: AbortController | undefined;
    let refresh: ReturnType<typeof setTimeout> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let lastEventId = '';
    const source = {close: () => {
      closed = true; controller?.abort(); clearTimeout(refresh); clearTimeout(retry);
    }};
    this.sources.set(url, source);
    let observers = 0;
    const observable = new Observable<EventSourceInterface<T>>(observer => {
      observers++;
      const listener = stream.subscribe(observer);
      return () => {
        listener.unsubscribe(); observers--;
        if (closable && observers === 0) {
          source.close(); this.sources.delete(url); this.streams.delete(url); this.observables.delete(url);
          this.eventSources = this.eventSources.filter(item => item !== source);
        }
      };
    });
    this.observables.set(url, observable);
    if (closable) this.eventSources.push(source);
    const connect = async () => {
      if (closed) return;
      controller = new AbortController();
      try {
        const headers: Record<string, string> = {};
        if (privateTopics.length) {
          const credential = await this.authorize(privateTopics);
          if (closed) return;
          headers.Authorization = 'Bearer ' + credential.token;
          refresh = setTimeout(() => {
            controller?.abort(); this.credentials.delete(JSON.stringify(privateTopics)); void connect();
          }, Math.max(1000, credential.expiresAt * 1000 - Date.now() - 60000 - Math.random() * 30000));
        }
        if (lastEventId) headers['Last-Event-ID'] = lastEventId;
        await fetchEventSource(url, {
          headers, signal: controller.signal, openWhenHidden: true,
          onopen: async response => {
            if (response.status === 401 || response.status === 403) {
              this.credentials.delete(JSON.stringify(privateTopics));
              throw new ForbiddenStream('Mercure access denied');
            }
            if (!response.ok || !response.headers.get('content-type')?.includes('text/event-stream')) throw new Error('Mercure unavailable');
          },
          onmessage: event => {
            if (event.id) lastEventId = event.id;
            if (!event.data) return;
            try {
              const data = JSON.parse(event.data);
              const payload = data.summary && data.type === 'Announce' ? JSON.parse(data.summary) : data;
              this.ngZone.run(() => stream.next(payload));
            } catch { /* A malformed event must not clear the displayed collection. */ }
          },
          onclose: () => { throw new Error('Mercure connection closed'); },
          onerror: error => {
            if (error instanceof ForbiddenStream) throw error;
            return 2000 + Math.random() * 3000;
          },
        });
      } catch (error) {
        clearTimeout(refresh);
        if (!closed && !(error instanceof ForbiddenStream) && ![401, 403].includes((error as any)?.status)) retry = setTimeout(() => void connect(), 3000 + Math.random() * 5000);
      }
    };
    this.ngZone.runOutsideAngular(() => void connect());
    return observable;
  }

  private async authorize(topics: string[]): Promise<{token: string; expiresAt: number}> {
    const key = JSON.stringify(topics);
    const cached = this.credentials.get(key);
    if (cached) {
      const value = await cached;
      if (value.expiresAt * 1000 > Date.now() + 90000) return value;
      this.credentials.delete(key);
    }
    if (!this.credentials.has(key)) {
      const request = firstValueFrom(this.http.post<{token: string; expiresAt: number}>(environment.apiUrl + '/realtime/mercure-token', {topics}, {
        headers: new HttpHeaders({'x-cache-bypass': 'true'}),
      })).catch(error => { this.credentials.delete(key); throw error; });
      this.credentials.set(key, request);
    }
    return this.credentials.get(key)!;
  }

  public closeArrayOfTopics(topics: string[], ngswBypass = true): void {
    const url = this.url(topics.map(topic => topic.replace(/^topic=/, '')), ngswBypass);
    const source = this.sources.get(url);
    source?.close(); this.sources.delete(url);
    this.streams.get(url)?.complete(); this.streams.delete(url); this.observables.delete(url);
    this.eventSources = this.eventSources.filter(item => item !== source);
  }

  public closeAll(): void {
    this.sources.forEach(source => source.close());
    this.streams.forEach(stream => stream.complete());
    this.sources.clear(); this.streams.clear(); this.observables.clear(); this.credentials.clear(); this.eventSources = [];
  }
}
